import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ImageUrl } from 'src/constants/imagesConst';
import { RoleContext } from 'src/context/roleContext';
import ChangePasswordDialog from 'src/components/dialogs/user/ChangePasswordDialog';
import { API_BASE_URL } from 'src/apiConfig';

/* PROFIL STRANA */
const ProfilePage = () => {
  const { schoolId, userId } = useContext(RoleContext);
  const [profile, setProfile] = useState({
    name: '',
    zipcode: '',
    street: '',
    telephone: '',
    email: '',
    professor: ''
  });
  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const { t } = useTranslation();
  const [username, setUserName] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/schools.php?id=${schoolId}`
        );
        const data = response.data;
        //get from lcoal storage
        const usernameForDisplay = localStorage.getItem('username');
        setUserName(usernameForDisplay || '');
        setProfile({
          name: data.name || '',
          zipcode: data.zipcode || '',
          street: data.street || '',
          telephone: data.telephone || '',
          email: data.email || '',
          professor: data.professor || ''
        });
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfile();
  }, [schoolId, userId]);

  const validateProfile = () => {
    let valid = true;
    let errorMessages = {};
    for (const field in profile) {
      if (!profile[field] && field !== 'professor') {
        errorMessages[field] = t('profile.validationError');
        valid = false;
      }
    }

    setErrorMessages(errorMessages);
    return valid;
  };

  const handleUpdateProfile = async (event) => {
    event.preventDefault();

    if (!validateProfile()) {
      setErrorSnackbarOpen(true);
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/api/schools.php?id=${schoolId}`,
        profile
      );
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrorSnackbarOpen(true);
    }
  };

  const handleClickOpen = () => {
    setOpenChangePassword(true);
  };

  const handleClose = () => {
    setOpenChangePassword(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleErrorSnackbarClose = () => {
    setErrorSnackbarOpen(false);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        background: `url(${ImageUrl.mathjpg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        py: 4,
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.9)',
          zIndex: 1
        }
      }}
    >
      <Paper
        elevation={5}
        sx={{
          padding: 4,
          borderRadius: '16px',
          backgroundColor: '#fff',
          maxWidth: '600px',
          margin: 'auto',
          position: 'relative',
          zIndex: 2,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 15px 35px rgba(0, 0, 0, 0.2)',
            transform: 'translateY(-5px)',
            transition: 'all 0.3s ease'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontFamily: '"Roboto", sans-serif',
              fontWeight: 700,
              color: '#333'
            }}
          >
            {t('profile.title')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClickOpen}
            sx={{
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '0.875rem',
              boxShadow: 3,
              backgroundColor: '#FFDB58',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#0056b3',
                boxShadow: '0 8px 15px rgba(0, 0, 0, 0.2)'
              }
            }}
          >
            {t('profile.changePassword')}
          </Button>
        </Box>

        <Box mb={3}>
          <Typography variant="h6" component="div">
            {t('profile.username')}:
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{ fontWeight: 'bold', fontFamily: '"Roboto", sans-serif' }}
          >
            {username}
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleUpdateProfile}
          noValidate
          autoComplete="off"
        >
          <TextField
            fullWidth
            label={t('profile.name')}
            margin="normal"
            variant="outlined"
            value={profile.name}
            onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            error={Boolean(errorMessages.name)}
            helperText={errorMessages.name}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
          />
          <TextField
            fullWidth
            label={t('profile.zipcode')}
            margin="normal"
            variant="outlined"
            value={profile.zipcode}
            onChange={(e) =>
              setProfile({ ...profile, zipcode: e.target.value })
            }
            error={Boolean(errorMessages.zipcode)}
            helperText={errorMessages.zipcode}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
          />
          <TextField
            fullWidth
            label={t('profile.address')}
            margin="normal"
            variant="outlined"
            value={profile.street}
            onChange={(e) => setProfile({ ...profile, street: e.target.value })}
            error={Boolean(errorMessages.street)}
            helperText={errorMessages.street}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
          />
          <TextField
            fullWidth
            label={t('profile.phone')}
            margin="normal"
            variant="outlined"
            value={profile.telephone}
            onChange={(e) =>
              setProfile({ ...profile, telephone: e.target.value })
            }
            error={Boolean(errorMessages.telephone)}
            helperText={errorMessages.telephone}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
          />
          <TextField
            fullWidth
            disabled={true}
            label={t('profile.email')}
            margin="normal"
            variant="outlined"
            value={profile.email}
            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
            error={Boolean(errorMessages.email)}
            helperText={errorMessages.email}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
          />
          <TextField
            fullWidth
            label={t('profile.professor')}
            margin="normal"
            variant="outlined"
            value={profile.professor}
            onChange={(e) =>
              setProfile({ ...profile, professor: e.target.value })
            }
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px'
              }
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: '#2596be',
              mt: 2,
              width: '100%',
              height: '50px',
              fontSize: '1rem',
              borderRadius: '20px',
              textTransform: 'none',
              boxShadow: '0 8px 15px rgba(0, 0, 0, 0.2)',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#0056b3',
                boxShadow: '0 15px 30px rgba(0, 0, 0, 0.3)',
                transform: 'translateY(-3px)'
              }
            }}
          >
            {t('profile.saveChanges')}
          </Button>
        </Box>
      </Paper>

      <ChangePasswordDialog
        open={openChangePassword}
        onClose={handleClose}
        username={username}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: '100%' }}
        >
          {t('profile.updateSuccess')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleErrorSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleErrorSnackbarClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          {t('profile.updateError')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;
